// frontend/src/components/Cart/Cart.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../hooks/useLanguage';
import { Video } from 'lucide-react';
import { OnlineBadge } from '../UI/OnlineBadge';

const Cart: React.FC = () => {
  const { t } = useLanguage();
  const { items, removeItem, totalPrice, isOpen, closeCart, getChildren } = useCart();
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      window.removeEventListener('resize', setVh);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Only top-level items (not companions)
  const rootItems = items.filter((item) => !item.parent_id);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={closeCart} />

      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 w-full sm:w-[400px] bg-[#edeef2] dark:bg-[#1f1f1f] z-50 flex flex-col sm:rounded-l-3xl shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)]" style={{ height: 'calc(var(--vh, 1vh) * 100)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-9 pt-9 pb-4 shrink-0">
          <div className="flex items-center gap-3">
            {/* Cart icon */}
            <svg className="w-6 h-6 text-[#0c63e9] dark:text-[#599bff] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2 className="text-[#0c63e9] dark:text-[#599bff] font-bold text-[18px] leading-[26px]">
              {t('priceList.cart.title')}
            </h2>
          </div>
          <button
            onClick={closeCart}
            className="w-6 h-6 flex items-center justify-center text-[#454545] dark:text-[#e4e4e4] hover:text-[#0c63e9] dark:hover:text-[#599bff] transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="mx-9 h-px bg-[#d3d3d3] dark:bg-[#2d3741] shrink-0" />

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-9 py-4 flex flex-col gap-3">
          {rootItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <svg className="w-12 h-12 text-[#454545]/20 dark:text-[#e4e4e4]/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-[#454545]/60 dark:text-[#e4e4e4]/60 text-base">{t('priceList.cart.empty')}</p>
              <div className="flex gap-3 items-center">
                <Link
                  to="/services"
                  onClick={closeCart}
                  className="bg-[#0c63e9] dark:bg-[#004dc3] text-[#eeeef0] font-medium text-base px-4 py-3 rounded-xl flex items-center gap-2 hover:bg-[#0b47d5] transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('home.sections.howToBook.ctaService')}
                </Link>
                <Link
                  to="/doctors"
                  onClick={closeCart}
                  className="border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] text-[#0c63e9] dark:text-[#599bff] font-medium text-base px-4 py-3 rounded-xl hover:bg-[#edeef2] dark:hover:bg-[#1f1f1f] transition-colors"
                >
                  {t('home.sections.howToBook.ctaDoctor')}
                </Link>
              </div>
            </div>
          ) : (
            rootItems.map((item) => {
              const companions = getChildren(item.id);
              return (
                <div
                  key={item.id}
                  className="bg-[#fdfdfd] dark:bg-[#121212] rounded-2xl px-4 py-3 flex flex-col gap-1.5"
                >
                  {/* Doctor name + delete button row */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {item.is_online && <OnlineBadge size="xs" />}
                      <p className="text-[#454545]/50 dark:text-[#e4e4e4]/50 text-xs leading-4 truncate">
                        {item.doctor_name ?? item.department_name ?? ''}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-5 h-5 flex items-center justify-center text-white bg-red-500 rounded shrink-0 hover:bg-red-600 transition-colors"
                      aria-label={t('priceList.removeFromList')}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Service name */}
                  <p className="text-[#454545] dark:text-[#e4e4e4] font-bold text-base leading-5 break-words">
                    {item.name}
                  </p>

                  {/* Companions */}
                  {companions.length > 0 && (
                    <div className="flex flex-col gap-0.5">
                      {companions.map((c) => (
                        <p key={c.id} className="text-[#454545]/60 dark:text-[#e4e4e4]/60 text-xs leading-4">
                          + {c.name}
                          {c.is_online && !item.is_online && (
                            <Video className="inline w-3 h-3 ml-1 -mt-0.5 text-[#0c63e9] dark:text-[#599bff]" aria-label={t('online.cartHint')} />
                          )}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Date, time & price row */}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    {item.appointment_date && item.appointment_time ? (
                      <p className="text-[#454545]/70 dark:text-[#e4e4e4]/70 text-sm leading-5">
                        {new Date(item.appointment_date).toLocaleDateString('ru-RU', {
                          day: '2-digit', month: '2-digit', year: 'numeric'
                        })}, {item.appointment_time}
                      </p>
                    ) : (
                      <span />
                    )}
                    {item.price > 0 && (
                      <span className="text-[#0c63e9] dark:text-[#599bff] font-bold text-base leading-5 whitespace-nowrap shrink-0">
                        {(Number(item.price) + companions.reduce((s, c) => s + Number(c.price), 0)).toLocaleString()} AMD
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {rootItems.length > 0 && (
          <div className="px-9 pb-9 pt-0 shrink-0 flex flex-col gap-4">
            {/* Divider */}
            <div className="h-px bg-[#d3d3d3] dark:bg-[#2d3741]" />

            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="text-[#454545] dark:text-[#e4e4e4] font-bold text-base leading-6">
                {t('priceList.total')}
              </span>
              <span className="text-[#0c63e9] dark:text-[#599bff] font-bold text-base leading-6">
                {Number(totalPrice).toLocaleString()} AMD
              </span>
            </div>

            {/* Price disclaimer */}
            <p
              onClick={() => setDisclaimerExpanded(v => !v)}
              className={`text-[#454545] dark:text-[#e4e4e4] text-[11px] leading-4 opacity-60 sm:line-clamp-none cursor-pointer sm:cursor-default ${disclaimerExpanded ? '' : 'line-clamp-1 sm:line-clamp-none'}`}
            >
              {t('servicesAndPrice.priceDisclaimer')}
            </p>

            {/* Continue button */}
            <button
              onClick={closeCart}
              className="border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] flex items-center justify-center h-[52px] rounded-xl text-[#0c63e9] dark:text-[#599bff] font-medium text-base hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors w-full"
            >
              {t('priceList.continueSelection')}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#d3d3d3] dark:bg-[#2d3741]" />
              <span className="text-[#454545]/40 dark:text-[#e4e4e4]/40 text-sm">{t('priceList.or')}</span>
              <div className="flex-1 h-px bg-[#d3d3d3] dark:bg-[#2d3741]" />
            </div>

            {/* Book button */}
            <Link
              to={(() => {
                const params = new URLSearchParams();
                params.set('services', items.filter(i => !i.parent_id).map(i => i.slug).join(','));
                const slotItem = items.find(i => i.appointment_date && i.appointment_time);
                if (slotItem) {
                  if (slotItem.doctor_slug) params.set('doctor', slotItem.doctor_slug);
                  params.set('date', slotItem.appointment_date!);
                  params.set('time', slotItem.appointment_time!);
                }
                return `/book-appointment?${params.toString()}`;
              })()}
              onClick={closeCart}
              className="bg-[#0c63e9] dark:bg-[#004dc3] flex items-center justify-center gap-2 h-[52px] rounded-xl text-[#eeeef0] font-medium text-base hover:opacity-90 transition-opacity w-full"
            >
              {t('doctors.bookAppointmentShort')}
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
